import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkle } from "@phosphor-icons/react";

/**
 * Unified agent header - back link + agent identity (left) + segmented two-tab
 * control (right). Replaces the older INPUT/OUTPUT stage badges.
 *
 * Tabs:
 *   tab1 = "Beállítás" (input/setup screen)
 *   tab2 = deliverable name e.g. "Márkahang", "Avatar", "Ajánlat"
 *
 * The active state slides between the two tabs via an absolutely positioned
 * background element (left/width animated). Tab labels themselves stay
 * transparent - the slider provides the highlight.
 */
export default function AgentTabHeader({
  agentName,
  deliverableLabel = "Output",
  accent = "#5e50f7",
  activeTab = "tab1",
  onTabChange,
  tab2Disabled = false,
}) {
  const tab1Ref = useRef(null);
  const tab2Ref = useRef(null);
  const [dims, setDims] = useState({ left1: 0, w1: 0, left2: 0, w2: 0 });

  const recompute = () => {
    const t1 = tab1Ref.current;
    const t2 = tab2Ref.current;
    if (!t1 || !t2) return;
    setDims({
      left1: t1.offsetLeft,
      w1: t1.offsetWidth,
      left2: t2.offsetLeft,
      w2: t2.offsetWidth,
    });
  };

  // Recompute on mount, on label changes, and on tab switch (font-weight
  // change on the active tab subtly shifts widths).
  useLayoutEffect(() => {
    recompute();
  }, [agentName, deliverableLabel, activeTab, tab2Disabled]);

  useEffect(() => {
    const onResize = () => recompute();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sliderLeft = activeTab === "tab1" ? dims.left1 : dims.left2;
  const sliderWidth = activeTab === "tab1" ? dims.w1 : dims.w2;

  return (
    <header
      className="sticky top-0 z-40 flex items-center justify-between gap-4"
      style={{
        padding: "14px 22px",
        background: "#0d0d16",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
      data-testid="agent-tab-header"
    >
      {/* Left cluster */}
      <div className="flex items-center gap-3 min-w-0">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 transition hover:text-white"
          style={{ fontSize: 12, color: "#7a7a95" }}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={13} weight="bold" />
          Vissza
        </Link>
        <span style={{ width: 1, height: 14, background: "rgba(255,255,255,0.10)" }} />
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: `linear-gradient(135deg, ${accent}, #4e3fd8)`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 14px -6px ${accent}aa`,
          }}
          aria-hidden="true"
        >
          <Sparkle size={13} weight="fill" color="white" />
        </span>
        <span
          className="truncate"
          style={{ fontSize: 14, color: "white", fontWeight: 500, letterSpacing: "-0.005em" }}
          data-testid="agent-name"
        >
          {agentName}
        </span>
      </div>

      {/* Right: segmented control */}
      <div
        style={{
          height: 38,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 8,
          padding: 3,
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
        data-testid="header-tabs"
      >
        {/* Animated slider - sits behind the labels, glides between tabs */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 3,
            bottom: 3,
            left: sliderLeft,
            width: sliderWidth,
            background: "rgba(124,108,242,0.18)",
            border: "1px solid rgba(124,108,242,0.28)",
            borderRadius: 6,
            transition: "left 0.22s ease-out, width 0.22s ease-out",
            zIndex: 1,
            pointerEvents: "none",
            opacity: sliderWidth > 0 ? 1 : 0,
          }}
          data-testid="tab-slider"
        />

        <TabButton
          ref={tab1Ref}
          active={activeTab === "tab1"}
          disabled={false}
          onClick={() => onTabChange?.("tab1")}
          testId="tab-beallitas"
        >
          Beállítás
        </TabButton>

        <TabButton
          ref={tab2Ref}
          active={activeTab === "tab2"}
          disabled={tab2Disabled}
          onClick={() => !tab2Disabled && onTabChange?.("tab2")}
          title={tab2Disabled ? "Először töltsd ki a Beállítást" : undefined}
          testId="tab-deliverable"
        >
          {deliverableLabel}
        </TabButton>
      </div>
    </header>
  );
}

const TabButton = React.forwardRef(function TabButton(
  { active, disabled, children, onClick, title, testId },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      style={{
        padding: "5px 14px",
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 6,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "color 0.18s ease-out, opacity 0.18s ease-out",
        zIndex: 2,
        position: "relative",
        background: "transparent",
        border: "1px solid transparent",
        color: active ? "white" : "#7a7a95",
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? "none" : "auto",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#7c6cf2",
            boxShadow: "0 0 6px rgba(124,108,242,0.7)",
          }}
        />
      )}
      {children}
    </button>
  );
});
